import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { ThemeMode } from '@bala/core';

/**
 * Thème et couleur d'accent. La couleur est injectée en HSL décomposé afin que
 * les variantes (survol, actif, versions douces) se calculent en CSS sans avoir
 * à générer une palette complète côté JavaScript.
 */

export interface ThemeState {
  mode: ThemeMode;
  /** Couleur d'accent en hexadécimal, ex. `#2C5C68`. */
  accent: string;
  /** Thème effectivement appliqué après résolution de `system`. */
  resolved: 'light' | 'dark';
  setMode: (mode: ThemeMode) => void;
  setAccent: (hex: string) => void;
}

const ThemeContext = createContext<ThemeState | null>(null);

export function useTheme(): ThemeState {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useTheme doit être utilisé dans un <ThemeProvider>.');
  return context;
}

/** Convertit `#rrggbb` en composantes HSL (teinte en degrés, saturation et luminosité en %). */
export function hexToHsl(hex: string): { h: number; s: number; l: number } | null {
  const match = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!match) return null;
  const int = Number.parseInt(match[1]!, 16);
  const r = ((int >> 16) & 255) / 255;
  const g = ((int >> 8) & 255) / 255;
  const b = (int & 255) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const delta = max - min;
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;

  if (delta !== 0) {
    s = delta / (1 - Math.abs(2 * l - 1));
    if (max === r) h = ((g - b) / delta) % 6;
    else if (max === g) h = (b - r) / delta + 2;
    else h = (r - g) / delta + 4;
    h *= 60;
    if (h < 0) h += 360;
  }

  return { h: Math.round(h), s: Math.round(s * 100), l: Math.round(l * 100) };
}

/**
 * Retour HSL → hex, pour repasser par `relativeLuminance` après application du
 * plancher de clarté du thème sombre.
 */
function hslToHex(h: number, s: number, l: number): string {
  const sn = s / 100;
  const ln = l / 100;
  const c = (1 - Math.abs(2 * ln - 1)) * sn;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = ln - c / 2;
  const [r, g, b] =
    h < 60 ? [c, x, 0]
    : h < 120 ? [x, c, 0]
    : h < 180 ? [0, c, x]
    : h < 240 ? [0, x, c]
    : h < 300 ? [x, 0, c]
    : [c, 0, x];
  const octet = (value: number) =>
    Math.round((value + m) * 255).toString(16).padStart(2, '0');
  return `#${octet(r)}${octet(g)}${octet(b)}`;
}

/** Luminance relative WCAG, pour choisir un texte noir ou blanc sur l'accent. */
function relativeLuminance(hex: string): number {
  const match = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!match) return 0;
  const int = Number.parseInt(match[1]!, 16);
  const channels = [(int >> 16) & 255, (int >> 8) & 255, int & 255].map((value) => {
    const c = value / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * channels[0]! + 0.7152 * channels[1]! + 0.0722 * channels[2]!;
}

/**
 * Encre ou blanc, selon lequel des deux contraste le mieux avec l'accent.
 *
 * Le choix se faisait sur un seuil de luminance à 0,55, deviné plutôt que
 * calculé. Il tombait faux exactement là où ça compte : l'accent du thème
 * sombre a une luminance de 0,31, donc sous le seuil, donc du blanc — mesuré à
 * 2,88:1 sur tous les boutons `variant="primary"`. L'encre y donne 5,80.
 *
 * Le point de bascule réel entre ces deux couleurs est à 0,207, mais le poser
 * en dur reproduirait la même fragilité : on compare les deux contrastes.
 */
function bestContrast(hex: string): string {
  const l = relativeLuminance(hex);
  const contre = (autre: number) => {
    const [haut, bas] = l > autre ? [l, autre] : [autre, l];
    return (haut + 0.05) / (bas + 0.05);
  };
  const BLANC = 1;
  const ENCRE = relativeLuminance('#1c1c1e');
  return contre(BLANC) >= contre(ENCRE) ? '#ffffff' : '#1c1c1e';
}

/** Le sarcelle de la corde de l'emblème de l'unité. */
export const DEFAULT_ACCENT = '#2C5C68';

/**
 * Couleurs d'accent proposées dans les paramètres. Les trois premières sont
 * relevées sur l'emblème : c'est la palette de l'unité, et elle vient en tête
 * parce que c'est celle qui s'accorde avec le reste de l'interface.
 */
export const ACCENT_PRESETS: Array<{ name: string; hex: string }> = [
  { name: 'Sarcelle', hex: '#2C5C68' },
  { name: 'Rouge Baladin', hex: '#B42828' },
  { name: 'Or', hex: '#A87F1E' },
  // Les cinq suivantes sont volontairement rabattues : les couleurs vives
  // d'origine, posées à côté des trois teintes de l'emblème, donnaient une
  // rangée qui n'avait plus l'air d'une palette.
  { name: 'Bleu', hex: '#1F5FA8' },
  { name: 'Indigo', hex: '#45508F' },
  { name: 'Prune', hex: '#7A4A78' },
  { name: 'Vert', hex: '#2E7D4F' },
  { name: 'Orange', hex: '#C2610A' },
];

export function ThemeProvider({
  children,
  storageKey = 'bala.theme',
}: {
  children: ReactNode;
  storageKey?: string;
}) {
  const [mode, setModeState] = useState<ThemeMode>(() => {
    if (typeof localStorage === 'undefined') return 'system';
    const stored = localStorage.getItem(`${storageKey}.mode`);
    return stored === 'light' || stored === 'dark' || stored === 'system' ? stored : 'system';
  });

  const [accent, setAccentState] = useState<string>(() => {
    if (typeof localStorage === 'undefined') return DEFAULT_ACCENT;
    return localStorage.getItem(`${storageKey}.accent`) ?? DEFAULT_ACCENT;
  });

  const [systemDark, setSystemDark] = useState(
    () => typeof window !== 'undefined' && window.matchMedia?.('(prefers-color-scheme: dark)').matches,
  );

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const query = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (event: MediaQueryListEvent) => setSystemDark(event.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  const resolved: 'light' | 'dark' = mode === 'system' ? (systemDark ? 'dark' : 'light') : mode;

  // Application au document : classe `.dark` + variables d'accent.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const root = document.documentElement;
    root.classList.toggle('dark', resolved === 'dark');
    root.style.colorScheme = resolved;

    const hsl = hexToHsl(accent);
    if (hsl) {
      root.style.setProperty('--accent-h', String(hsl.h));
      root.style.setProperty('--accent-s', `${hsl.s}%`);
      // En thème sombre, un accent trop foncé devient illisible : plancher à 52 %.
      const lightness = resolved === 'dark' ? Math.max(52, hsl.l) : hsl.l;
      root.style.setProperty('--accent-l', `${lightness}%`);
      /*
       * La couleur de contraste se calcule sur l'accent RÉELLEMENT appliqué, et
       * non sur le hex d'origine.
       *
       * Le plancher ci-dessus éclaircit l'accent en sombre : l'accent
       * `#2C5C68` devient un teal clair, sur lequel le blanc ne tenait plus
       * que 2,4:1. Tous les boutons `variant="primary"` du thème sombre
       * étaient concernés — le composant le plus employé de l'interface.
       */
      const applique = hslToHex(hsl.h, hsl.s, lightness);
      root.style.setProperty('--accent-contrast', bestContrast(applique));
    }

    // Couleur de la barre d'état iOS / Android en mode installé : elle doit
    // suivre le fond réel de l'application, sinon une bande grise apparaît
    // au-dessus du beige une fois l'application installée.
    const themeMeta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
    if (themeMeta) themeMeta.content = resolved === 'dark' ? '#0e1a1f' : '#f2ebd9';
  }, [resolved, accent]);

  const setMode = useCallback(
    (next: ThemeMode) => {
      setModeState(next);
      if (typeof localStorage !== 'undefined') localStorage.setItem(`${storageKey}.mode`, next);
    },
    [storageKey],
  );

  const setAccent = useCallback(
    (hex: string) => {
      if (!hexToHsl(hex)) return;
      setAccentState(hex);
      if (typeof localStorage !== 'undefined') localStorage.setItem(`${storageKey}.accent`, hex);
    },
    [storageKey],
  );

  const value = useMemo<ThemeState>(
    () => ({ mode, accent, resolved, setMode, setAccent }),
    [mode, accent, resolved, setMode, setAccent],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}
